import { useEffect, useState } from 'react'
import type { AgentChatAttachment } from '@/frontend/pages/Dashboard/AgentChats/types'
import { attachmentUrl, MAX_DOCUMENT_PREVIEW_BYTES } from '@/frontend/features/AgentChats/artifacts/documentFormat'

export function useDocumentText(attachment: AgentChatAttachment, active: boolean) {
  const [attempt, setAttempt] = useState(0)
  const [result, setResult] = useState<{ key: string; text?: string; failed?: boolean } | null>(null)
  const url = attachmentUrl(attachment.url, 'text')
  const key = `${url}:${attachment.id}:${attempt}`
  const tooLarge = attachment.byte_size > MAX_DOCUMENT_PREVIEW_BYTES

  useEffect(() => {
    if (!active || tooLarge) return
    const controller = new AbortController()
    fetch(url, { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'text/plain' } })
      .then(async response => {
        // Auth redirects must not be displayed as document contents.
        if (!response.ok || response.redirected || !response.headers.get('content-type')?.startsWith('text/plain')) throw new Error('preview')
        const text = await response.text()
        if (new Blob([text]).size > MAX_DOCUMENT_PREVIEW_BYTES) throw new Error('preview-size')
        if (!controller.signal.aborted) setResult({ key, text })
      })
      .catch(() => { if (!controller.signal.aborted) setResult({ key, failed: true }) })
    return () => controller.abort()
  }, [active, tooLarge, url, key])

  // Key the read by attachment so a late result can never show another file.
  return { text: result?.key === key ? result.text : undefined,
    failed: result?.key === key && result.failed, tooLarge, retry: () => setAttempt(value => value + 1) }
}
